import { action, observable } from 'mobx';
import { ProjectsStore } from './ProjectsStore';
import { appConfig } from 'cd-eft/config/AppConfig';
import { esriProjection, Geometry, Home, MapView } from 'cd-eft/utils/esriTypes';
import Viewpoint from '@arcgis/core/Viewpoint';
import { AppConstants } from 'cd-eft/appConstants';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { TerraceVertexParamsDTO, UgoVertexParamsDTO } from "cd-eft/_codegen_/openapi-generator/eft-set";
import { testDecimalString } from 'cd-eft/utils/stringUtilities';
class DesignProjectsStore extends ProjectsStore {
  constructor(private designstore: any) {
    super(designstore);
  }
}
export interface INotification {
  message: string;
  header: string;
  type: string;
  hidden: boolean;
  timestamp: Date | undefined;
}
interface ISurface {
  sessionUuid: string;
  surfaceDefId: number;
  surfaceDefTitle: string;
  surfaceDefClassId: number;
  surfaceDefClassName: string;
  designElementId: number;
  borderVector: IBorderPoint[];
}

interface IBorderPoint {
  x: number;
  y: number;
  z: number;
}

interface ITerraceGroup {
  groupID: number,
  terraceVertexParamsDTOMap: Map<string, TerraceVertexParamsDTO>
}

interface IUGOGroup {
  groupID: number,
  ugoVertexParamsDTOMap: Map<string, UgoVertexParamsDTO>
}

export const structureTypes = {
  7: 'Grassed', // Waterway
  11: 'Rectangular Pit',
  12: 'Round Pit',
  13: 'Diked Pit',
  14: 'Pad',
  15: 'Land Leveling',
  19: 'Lined', // Waterway
  20: 'Gradient', // Terrace
  21: 'Level', // Terrace
  22: 'Storage', // Terrace
  23: 'Wascob', // Terrace
};

export class DesignStore {
  constructor(private appStore: any) {
    this.projectsStore = new DesignProjectsStore(this);
    this.init();
  }
  basemapGalleryWidget;
  layerListWidget;
  @observable coordinateWatchHandler;
  @observable projectsStore: DesignProjectsStore;
  @observable currentSurface = {} as ISurface;
  @observable surfaceExpressMode: boolean = appConfig.tempHardCodes.surfaceExpressMode;
  @observable showSettingsPopup: boolean = false;
  @observable baseGraphicCompleteHandle: any;
  @observable hasSurface: boolean = false;
  @observable sceneViewActive: boolean = false;
  @observable noProjectMessage: string = 'Please create, or load a project before continuing.';
  @observable noSurfaceMessage: string = 'This tool requires a surface!';
  @observable designSettingsTabs = ['General', 'Land Leveling', 'Border', 'AI'];
  @observable currentDesignSettingsTab: string = 'General';
  @observable isLoading: boolean = false;
  @observable loadingMsg: string = '';
  @observable snappingEnabled: boolean = false;
  @observable designElementTypeId: number = 0;
  @observable designElementId: number = 0;
  @observable surveyPointNames: any[] = [];
  @observable surveyPoints: any[] = [];
  @observable disableInput: boolean = true;
  @observable originalStation: any = {};
  @observable structureLayerExists: boolean = true;
  @observable reachStationsLayerExists: boolean = false;
  @observable wdtGroupsLength: number = 0;
  @observable terraceGroups: ITerraceGroup[] = [];
  @observable terraceGroupsLength: number = 0;
  @observable ugoGroups: IUGOGroup[] = [];
  @observable ugoGroupsLength: number = 0;
  @observable breakLineGroupsLength: number = 0;
  @observable toggleResetValues: boolean = false;
  @observable allFieldsValids: boolean = true;
  @observable currentDesignElementJson: any = null;
  @observable onNewStructureTab: boolean = false;
  @observable disableSaveButtonInputPage: boolean = true;
  @observable disableSaveProceedButtonInputPage: boolean = true;
  @observable disableCalculateButtonDesignPage: boolean = false; // control save button in waterway design 
  @observable DisableSaveButtonDesignPage: boolean = false; // control save&calculate button in waterway design 
  @observable hasWaterwayCalculations: boolean = false; // control generate surface button in waterway design 
  @observable commonHorizontalStretchFactor: number | undefined = undefined; // Waterways, Terraces, and UGO (for profiles where only one instance at a time is possible)
  @observable commonVerticalStretchFactor: number | undefined = undefined; // Waterways, Terraces, and UGO (for profiles where only one instance at a time is possible)
  @observable tcs1HorizontalStretchFactor: number | undefined = undefined;
  @observable tcs2HorizontalStretchFactor: number | undefined = undefined;
  @observable tcs3HorizontalStretchFactor: number | undefined = undefined;
  @observable singleCrossSectionDesignElementId: number = 0;
  // @observable flipEditsFlags: boolean = false;
  @observable displayResultBanner: boolean = false;
  @observable toggle3DMap: boolean = false;
  @observable contourInterval: number = 2.0;
  @observable processName: string = '';
  @observable loadingProgress: number = 0;
  @observable setTerraceSecLayerVisibility: boolean = true;
  @observable setUgoSecLayerVisibility: boolean = true;
  @observable alignmentProfileStations: any = [];

  private loaderStack: string[] = [];

  maxAddBorderPointDistance = 300;
  maxDeleteBorderPointDistance = 300;
  shouldHumanFeedbackOn: boolean = false;
  shouldAiZoomToAction: boolean = false;


  @action.bound setLoader(message: string = '', processName?: string, progressStart?: number, progressEnd?: number) {
    const token = "token";
    if (processName || message) {
      this.loaderStack.push(token);
      this.isLoading = true;
      this.loadingMsg = message;
      if (processName) this.processName = processName;
      if (progressStart !== undefined && progressEnd !== undefined) {
        this.loadingProgress = progressStart;
        this.animateProgress(progressStart, progressEnd);
      } else {
        this.loadingProgress = 0;
      }
    } else {
      this.loaderStack.pop();
      this.isLoading = this.loaderStack.length > 0;
      if (!this.isLoading) {
        this.loadingMsg = '';
        this.processName = '';
        this.isLoading = false;
        this.loadingProgress = 0;
      }
    }
  }

  private async animateProgress(start: number, end: number) {
    const duration = 300;
    const steps = end - start;
    const stepDuration = duration / steps;
    for (let i = start; i <= end; i++) {
      this.loadingProgress = i;
      await new Promise(resolve => setTimeout(resolve, stepDuration));
    }
  }

  @observable autoProfileZoom: boolean = true;
  @observable hideCrossSectionToolbar: boolean = true;
  @observable hideSingleCrossSection: boolean = true;
  @observable hideThreeCrossSection: boolean = true;
  @observable gpServiceSessionID: string = '';
  @observable crossSectionDrawingComplete: boolean = false;
  public homeWidget?: Home;
  public drawingTypeByDesignElementTypeId = {
    2: {
      Id: 1,
      name: "Drawings"
    },
    4: {
      Id: 107,
      name: "Terrace drawings"
    },
    5: {
      Id: 104,
      name: "Alignment drawings"
    },
    7: {
      Id: 105,
      name: "Waterway drawings"
    },
    11: {
      Id: 101,
      name: "Pit drawings"
    },
    12: {
      Id: 101,
      name: "Pit drawings"
    },
    13: {
      Id: 101,
      name: "Pit drawings"
    },
    14: {
      Id: 102,
      name: "Pad drawings"
    },
    15: {
      Id: 103,
      name: "Land leveling drawings"
    },
    25: {
      Id: 109,
      name: "Underground_Outlet drawings"
    },
  }

  @action reset() {
    this.init();
  }

  @action init() {
    this.resetSurface();
    this.surfaceExpressMode = appConfig.tempHardCodes.surfaceExpressMode;
    this.showSettingsPopup = false;
    this.hasSurface = false;
    this.sceneViewActive = false;
    this.designElementTypeId = 0;
    this.gpServiceSessionID = '';
    this.wdtGroupsLength = 0;
    this.terraceGroupsLength = 0;
    this.currentDesignElementJson = null;
    this.contourInterval = 2.0;
    this.resetProfileStretchFactors();
    this.breakLineGroupsLength = 0;
    this.alignmentProfileStations = [];
  }

  @action resetSurface() {
    this.currentSurface = {
      sessionUuid: '',
      surfaceDefId: 0,
      surfaceDefTitle: '',
      surfaceDefClassId: 1,
      surfaceDefClassName: '',
      designElementId: 0,
      borderVector: [],
    };
  }

  @action resetProfileStretchFactors() {
    this.commonHorizontalStretchFactor = undefined;
    this.commonVerticalStretchFactor = undefined;
    this.tcs1HorizontalStretchFactor = undefined;
    this.tcs2HorizontalStretchFactor = undefined;
    this.tcs3HorizontalStretchFactor = undefined;
  }

  @action hasProject() {
    if (this.projectsStore.currentProject.projectUuid === '') {
      return false;
    } else {
      return true;
    }
  }

  @action hasSurfaceTest() {
    if (this.currentSurface.designElementId === 0) {
      return false;
    } else {
      return true;
    }
  }

  setHomeWidgetTargetExtent(extent: Geometry) {
    if (this.homeWidget) {
      if (extent) {
        const targetExtent = esriProjection.project(extent, this.homeWidget.view.spatialReference) as Geometry;
        const viewpoint = new Viewpoint();
        viewpoint.targetGeometry = targetExtent;
        this.homeWidget.viewpoint = viewpoint;

        // console.log('Updated Home Extent!');
      }
    } else {
      console.warn('Warning: Home widget is not set.');
    }
  }

  @action removeDesignedContours(map) {
    let designedContourLayer;
    let originalContourLayer;
    if (map) {
      designedContourLayer = map.findLayerById(AppConstants.DesignedContourLayerId);
      originalContourLayer = map.findLayerById(AppConstants.OriginalContourLayerId);
    }
    if (designedContourLayer && originalContourLayer && map) {
      map.remove(designedContourLayer);
      designedContourLayer.destroy();
      originalContourLayer.visible = true;
    }
  }

  @action async validateSingleCrossSection(mapView: MapView, errorCode: string = 'CS_1') {
    const structureLayers = mapView.map.allLayers.filter((layer) => {
      return layer.id.includes("intersect_border");
    });
    if (structureLayers && structureLayers.length > 0) {
      this.structureLayerExists = true;
    }
    else {
      if (this.structureLayerExists === true) {
        await MessageCenter.addErrorFromCode(errorCode);
        this.structureLayerExists = false;
      }
    }
  }
}

export const handleActionValueInputChange = async (event: any, vm) => {
  vm.changedInputValue = true;
  const { value, name } = event.target;

  if (name === 'fixedValue') {
    if (value.length > 0) {
      // Disallow negative numbers, letters, and '+' character for fixed value
      if (!testDecimalString(value)) {
        return;
      }
    }
    vm.fixedValue = value;
  } else if (name === 'shiftElevation') {
    if (value.length > 0) {
      // Disallow letters, and '+' character for fixed value
      if (!/^-?(\d+(\.\d*)?|\.\d*|)$/.test(value)) {
        return;
      }
    }
    vm.shiftElevation = value;
  }
}

export const updateStation = (item, stations, index: number) => {
  // Update station with edited values
  const updatedStation = { ...stations[index] };
  for (const key of Object.keys(item)) {
    if (key.includes('.')) {
      const [parentKey, childKey] = key.split('.');
      if (!updatedStation[parentKey]) {
        updatedStation[parentKey] = {};
      }
      updatedStation[parentKey][childKey] = item[key];
    } else {
      updatedStation[key] = item[key];
    }
  }
  stations[index] = updatedStation;
}

export const shiftElevationCallback = async (errorCode, shiftElevationFunc, cancelFunc) => {
  await MessageCenter.addWarningFromCode(errorCode, [
    {
      callback: () => {
        MessageCenter.clearMessages();
        cancelFunc();
      },
      text: 'Cancel',
    },
    {
      callback: () => {
        MessageCenter.clearMessages();
        shiftElevationFunc();
      },
      text: 'OK',
    },
  ]);
}
